import React, { ReactNode, useEffect, useMemo, useState } from "react";

import { Col, Container, Row } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";

import type { OperationVariables } from "@apollo/client";
import dayjs from "dayjs";
import type { SetterOrUpdater } from "recoil";

import type { FilterBy, ListGroupedResalesQuery, ListGroupedResalesQueryVariables, ResaleFragment } from "@api/graphql";
import { UserDisplay } from "@components/UserDisplay";
import DateFilter from "@components/table/DateFilter";
import SearchInput from "@components/table/SearchInput";
import { DetailsDrawer } from "@pages/dashboard/DetailsDrawer";
import type { SelectedResalesType } from "@states/resale_submission";
import { type PaginatedOutputGeneric, getPageHeading, useWatch } from "@util/index";
import type { QueryFunction, QueryOptions } from "@util/query";
import type { ResaleGroup } from "@util/resale";

import { CustomAccordion } from "../accordion/CustomAccordion";
import { HorizontalButtonPager } from "../table/Pager";
import { ProvisionalResaleTable } from "../table/ProvisionalResaleTable";
import type { TableHeader } from "../table/TableContents";
import { TableFilter, type TableFilterInput } from "../table/TableFilter";

import styles from "./GroupedResales.module.scss";

const RESALE_STATUS: keyof ResaleFragment = "resale_status";
const PAYMENT_STATUS: keyof ResaleFragment = "payment_status";

export type groupedResalesSourcePageType =
  | "UnderReviewResales"
  | "ReportsToReview"
  | "PendingPayments"
  | "HoldPeriod"
  | "PastReports"
  | "PastResaleReports";

interface GroupedResalesProps<TData, TVariables extends OperationVariables> {
  queryFunction: QueryFunction<TData, TVariables>;
  getQueryVariables: (page: number) => TVariables;
  getResaleGroupsFromQueryData: (data: TData) => ResaleGroup[];
  getPaginatedOutputFromQueryData: (data: TData) => PaginatedOutputGeneric<ResaleGroup>;
  updateQueryVariablesWithFilter: (
    currentQueryVariables: TVariables,
    new_filter_by: FilterBy[] | undefined,
  ) => TVariables;
  getAccordionHeader?: (resaleGroup: ResaleGroup) => ReactNode;
  getTableToolbar?: (resaleGroup: ResaleGroup, refetch?: () => void) => ReactNode;
  customTableHeaders?: TableHeader<ResaleFragment>[];
  sourcePage?: groupedResalesSourcePageType;
  resaleStatusFilterOptions?: TableFilterInput[];
  paymentStatusFilterOptions?: TableFilterInput[];
  customTableStyleClassName?: string;
  showCheckBox?: boolean;
  rowCheckBoxIsDisabled?: (row: ResaleFragment) => boolean;
  handleHeaderCheckBoxChange?: (
    setRecoilSelectedRows: SetterOrUpdater<SelectedResalesType>,
    resales: ResaleFragment[],
  ) => (e: React.ChangeEvent<HTMLInputElement>, batchID: string) => void;
  displayBanner?: boolean;
}

export const GroupedResales = ({
  queryFunction,
  getQueryVariables,
  getResaleGroupsFromQueryData,
  getPaginatedOutputFromQueryData,
  updateQueryVariablesWithFilter,
  getAccordionHeader,
  getTableToolbar,
  customTableHeaders,
  sourcePage,
  resaleStatusFilterOptions,
  paymentStatusFilterOptions,
  customTableStyleClassName,
  showCheckBox,
  rowCheckBoxIsDisabled,
  handleHeaderCheckBoxChange,
  displayBanner = true,
}: GroupedResalesProps<ListGroupedResalesQuery, ListGroupedResalesQueryVariables>) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentSelectedRowData, setCurrentSelectedRowData] = useState<ResaleFragment>();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [groupedResalesData, setGroupedResalesData] = useState<ResaleGroup[]>([]);
  const [resaleStatusFilter, setResaleStatusFilter] = useState<FilterBy>();
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<FilterBy>();
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState<{ startDate: string | null; endDate: string | null }>({
    startDate: null,
    endDate: null,
  });

  const isNumeric = (value: string) => !Number.isNaN(Number(value)) && Number.isFinite(Number(value));

  // Combine filters into a single array, and ensure queryVariables updates correctly
  const combinedUserFilter = useMemo(() => {
    const filters: FilterBy[] = [];

    if (resaleStatusFilter) {
      filters.push(resaleStatusFilter);
    }

    if (paymentStatusFilter) {
      filters.push(paymentStatusFilter);
    }

    if (searchText) {
      if (isNumeric(searchText)) {
        filters.push({ field: "resale_id", eq: searchText });
      } else {
        filters.push({
          field: "artwork_name",
          OR: [
            { field: "artwork_name", iLike: searchText },
            { field: "artist_name", iLike: searchText },
            { field: "provisional_artist_name", iLike: searchText },
          ],
        });
      }
    }

    if (dateRange.startDate && dateRange.endDate) {
      filters.push({
        field: "reported_on",
        gte: dayjs(dateRange.startDate).toISOString(),
      });
      filters.push({
        field: "reported_on",
        lte: dayjs(dateRange.endDate).endOf("day").toISOString(),
      });
    }

    return filters;
  }, [resaleStatusFilter, paymentStatusFilter, searchText, dateRange]);

  const queryVariables = useMemo(() => {
    const baseVariables = getQueryVariables(page);
    return updateQueryVariablesWithFilter(baseVariables, combinedUserFilter);
  }, [getQueryVariables, page, updateQueryVariablesWithFilter, combinedUserFilter]);

  const queryOptions: QueryOptions<ListGroupedResalesQuery, ListGroupedResalesQueryVariables> = useMemo(
    () => ({
      variables: queryVariables,
      fetchPolicy: "no-cache",
      errorPolicy: "none",
    }),
    [queryVariables],
  );

  const { data, loading, refetch } = queryFunction(queryOptions);

  const paginatedOutput = data ? getPaginatedOutputFromQueryData(data) : undefined;

  useEffect(() => {
    if (data) {
      const resaleGroups = getResaleGroupsFromQueryData(data);
      setGroupedResalesData(resaleGroups);
      setTotalPages(paginatedOutput?.total_pages || 1);
      setTotalItems(paginatedOutput?.total_items || 0);

      // Reset the page to 1 if the current page exceeds total pages after filtering
      if (page > (paginatedOutput?.total_pages || 1)) {
        setPage(1);
      }
    }
  }, [data, getResaleGroupsFromQueryData, page, paginatedOutput?.total_pages, paginatedOutput?.total_items]);

  useEffect(() => {
    if (!currentSelectedRowData) {
      return;
    }

    const currentRowResaleId = currentSelectedRowData.resale_id;
    let currentUpdatedRow = undefined;

    for (const group of groupedResalesData) {
      currentUpdatedRow = group.resales.find(({ resale_id }) => resale_id === currentRowResaleId);

      if (currentUpdatedRow) {
        break;
      }
    }

    setCurrentSelectedRowData(currentUpdatedRow);
  }, [currentSelectedRowData, groupedResalesData]);

  useWatch([resaleStatusFilter, paymentStatusFilter, searchText, dateRange], () => {
    // Reset to page 1 when filters change and refetch data
    setPage(1);
    refetch();
  });

  const handleOnTableRowClick = (rowData: ResaleFragment) => {
    setCurrentSelectedRowData(rowData);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const pageHeading = getPageHeading(sourcePage, "GroupedResales");

  // Wrap text input with debounce to avoid firing a GraphQL request on every keypress

  const enterPressed = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSearchText(e.currentTarget.value);
    }
  };

  return (
    <>
      {currentSelectedRowData && (
        <DetailsDrawer
          resale={currentSelectedRowData}
          isDrawerOpen={isDrawerOpen}
          refetch={refetch}
          sourcePage={sourcePage ? sourcePage : "UnderReviewResales"}
          onHide={handleDrawerClose}
        />
      )}
      <Container className="p-4" fluid>
        {displayBanner && (
          <>
            <Row className="pb-4">
              <Col>
                <UserDisplay />
              </Col>
            </Row>
            <Row>
              <Col>
                <h3>{pageHeading.heading}</h3>
                <span className="b3">{pageHeading.subheading}</span>
              </Col>
            </Row>
          </>
        )}
        <Container fluid>
          <Row className="mt3">
            <Col md={3}>
              <SearchInput searchText={searchText} setSearchText={setSearchText} enterPressed={enterPressed} />
            </Col>
            <Col md={3}>
              <DateFilter setDateRange={setDateRange} />
            </Col>
            {resaleStatusFilterOptions ? (
              <Col md={3}>
                <TableFilter
                  field={RESALE_STATUS}
                  title="Resale status"
                  inputs={resaleStatusFilterOptions}
                  setFilter={setResaleStatusFilter}
                  showInvoiceRequestSentFilter={false}
                />
              </Col>
            ) : (
              <></>
            )}
            {paymentStatusFilterOptions ? (
              <Col md={3}>
                <TableFilter
                  field={PAYMENT_STATUS}
                  title="Payment Status"
                  inputs={paymentStatusFilterOptions}
                  setFilter={setPaymentStatusFilter}
                  showInvoiceRequestSentFilter={sourcePage === "HoldPeriod"}
                />
              </Col>
            ) : (
              <></>
            )}
          </Row>
        </Container>
        <Row className="mt-3">
          <Col>
            <Row>
              <Col>
                {loading && (
                  <div className="w-100 d-flex justify-content-center">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  </div>
                )}
                {!loading &&
                  groupedResalesData &&
                  groupedResalesData.map((resaleGroup, index) => {
                    const { batch_grouping_id, resales } = resaleGroup;
                    const isProvisionalEligibleExist = resales.some(
                      (resale) => resale.resale_status === "PROVISIONALLY_ELIGIBLE",
                    );
                    const resaleBatchData = { ...resaleGroup, isProvisionalEligibleExist };
                    return (
                      <div key={batch_grouping_id} className={styles["row-border"]}>
                        <CustomAccordion
                          accordionKey={`${batch_grouping_id}-${index}`}
                          header={getAccordionHeader?.(resaleGroup)}
                          customHeaderStyle="px-2"
                        >
                          <div className="p-2 pt-0 d-flex justify-content-start align-items-start align-self-stretch flex-column">
                            {getTableToolbar?.(resaleBatchData, refetch)}
                            <ProvisionalResaleTable
                              refetch={refetch}
                              onRowClick={handleOnTableRowClick}
                              resales={resales}
                              customTableHeaders={customTableHeaders}
                              customTableStyleClassName={customTableStyleClassName}
                              showCheckBox={showCheckBox}
                              rowCheckBoxIsDisabled={rowCheckBoxIsDisabled}
                              handleHeaderCheckBoxChange={handleHeaderCheckBoxChange}
                            />
                          </div>
                        </CustomAccordion>
                      </div>
                    );
                  })}
              </Col>
            </Row>
            <Row className="mt-1 mb-3">
              <Col>
                <HorizontalButtonPager page={page} totalItems={totalItems} totalPages={totalPages} setPage={setPage} />
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </>
  );
};
